// Signers the wallet holds.
//  * root: the phrase-derived Ed25519 key (#root), the DID's first update key.
//    A linked Ed25519 browser key also becomes an update key.
//  * browser: generated in WebCrypto, non-extractable. Ed25519 by default; on a
//    browser without WebCrypto Ed25519, P-256 as an assertion-only key that can
//    never update the DID (the control split, decided 2 Nov 2026).
import { ed25519 } from "@noble/curves/ed25519";
import { p256 } from "@noble/curves/p256";
import { ALG_FOR_KIND, type Signer } from "../crypto/jws";
import type { PublicKey } from "../crypto/keys";

export function rootSigner(seed: Uint8Array, kid: string): Signer {
  return { alg: "EdDSA", kid, publicKey: { kind: "Ed25519", raw: ed25519.getPublicKey(seed) },
           sign: async (m) => ed25519.sign(m, seed) };
}

export interface BrowserKey { kind: PublicKey["kind"]; pair: CryptoKeyPair; raw: Uint8Array }

export async function generateBrowserKey(subtle: SubtleCrypto = globalThis.crypto.subtle,
                                         preferP256 = false): Promise<BrowserKey> {
  if (!preferP256) {
    try {
      const pair = (await subtle.generateKey({ name: "Ed25519" }, false, ["sign", "verify"])) as CryptoKeyPair;
      const raw = new Uint8Array(await subtle.exportKey("raw", pair.publicKey));
      return { kind: "Ed25519", pair, raw };
    } catch { /* no WebCrypto Ed25519 in this browser: fall back to P-256 */ }
  }
  const pair = (await subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"])) as CryptoKeyPair;
  const uncompressed = new Uint8Array(await subtle.exportKey("raw", pair.publicKey));
  const raw = p256.ProjectivePoint.fromHex(uncompressed).toRawBytes(true);
  return { kind: "P-256", pair, raw };
}

export function browserSigner(key: BrowserKey, kid: string, subtle: SubtleCrypto = globalThis.crypto.subtle): Signer {
  const algo = key.kind === "Ed25519" ? { name: "Ed25519" } : { name: "ECDSA", hash: "SHA-256" };
  return { alg: ALG_FOR_KIND[key.kind], kid, publicKey: { kind: key.kind, raw: key.raw },
           sign: async (m) => new Uint8Array(await subtle.sign(algo, key.pair.privateKey, m)) };
}
